"use client";

import { fetchCollectionsData } from "@/lib/fetchData";
import { getClient, getIndex } from "@/server/meilisearch";
import { useMemo } from "react";
import useSWR from "swr";
import { Filter, specialChar } from "./search-content";
import { useUser } from "@/components/clients/LocaleToStorage";

export interface CategoryTree {
  id: number;
  title: string;
  level: number;
  parent: number | null;
  count: number;
  children: CategoryTree[];
}

// 组装树结构
function getTree(
  data: {
    id: number;
    name: string;
    level: number;
    parent: number | null;
    count: number;
    categoryName: string;
  }[],
  parentId: number | null,
  lang: number
): CategoryTree[] {
  return data
    .filter((item) => item.parent === parentId)
    .map((item) => ({
      id: item.id,
      level: item.level,
      parent: item.parent,
      title: item.categoryName,
      count: item.count,
      children: getTree(data, item.id, lang),
    }));
}

// 过滤技术文档
function filterTechDoc(res: Map<string, number>, userLevel?: number) {
  if (!userLevel) {
    const techDoc1 = res.get("techDoc1") || 0;
    const techDoc2 = res.get("techDoc2") || 0;
    const techDoc3 = res.get("techDoc3") || 0;

    // 找出最大值及其对应的级别
    const techDocValues = [
      { level: 1, value: techDoc1 },
      { level: 2, value: techDoc2 },
      { level: 3, value: techDoc3 },
    ];
    const maxTechDoc = techDocValues.reduce((max, current) =>
      current.value > max.value ? current : max
    );

    return {
      techDoc: maxTechDoc,
      appNote: res.get("appNote") || 0,
      evmGuide: res.get("evmGuide") || 0,
    };
  }

  const techDoc = res.get(`techDoc${userLevel}`) || 0;

  return {
    techDoc: {
      level: userLevel,
      value: techDoc,
    },
    appNote: res.get("appNote") || 0,
    evmGuide: res.get("evmGuide") || 0,
  };
}

/** 获取产品分类 */
async function getProductCategory(params: { lang: number; site: number }) {
  const res = await fetchCollectionsData<{
    id: number;
    name: string;
    level: number;
    parent: number | null;
    lang_data: {
      name: string;
      lang: string;
    }[];
  }>("product_category", {
    site: params.site.toString(),
    lang: params.lang.toString(),
    fields: [
      "id",
      "name",
      "level",
      "parent",
      "lang_data.name",
      "lang_data.lang",
    ],
    page: 1,
    limit: -1,
  });

  return res;
}

/** 获取应用分类 */
async function getApplicationCategory(params: { lang: number; site: number }) {
  const res = await fetchCollectionsData<{
    id: number;
    name: string;
    level: number;
    parent: number | null;
    lang_data: {
      name: string;
      lang: string;
    }[];
  }>("application_category", {
    site: params.site.toString(),
    lang: params.lang.toString(),
    fields: [
      "id",
      "name",
      "level",
      "parent",
      "lang_data.name",
      "lang_data.lang",
    ],
    page: 1,
    limit: -1,
  });

  return res;
}

/** 获取新闻分类 */
async function getNewsCategory(params: { lang: number; site: number }) {
  const res = await fetchCollectionsData<{
    id: number;
    name: string;
    lang_data: {
      name: string;
      lang: string;
    }[];
  }>("news_category", {
    site: params.site.toString(),
    lang: params.lang.toString(),
    fields: ["id", "name", "lang_data.name", "lang_data.lang"],
    page: 1,
    limit: -1,
  });

  return res;
}

/** 获取产品分类数量统计 */
async function getProductCategoryFacet(params: {
  lang: number;
  site: number;
  keyword: string;
  filter?: Filter[];
}) {
  const client = getIndex(params.site);

  const applicationFilter = params.filter?.find(
    (item) => item.type === "application"
  );

  const filterArray: string[] = ["type = 'product'"];

  if (applicationFilter && applicationFilter?.level === 1) {
    filterArray.push(`applicationCategory1 = '${applicationFilter.value}'`);
  }

  await client.updateFilterableAttributes([
    "newsCategory",
    "type",
    "applicationCategory1",
    "applicationCategory2",
    "productCategory1",
    "productCategory2",
    "productCategory3",
    "techDoc1",
    "techDoc2",
    "techDoc3",
    "appNote",
    "evmGuide",
  ]);

  // 过滤特殊字符
  const keywords = params.keyword
  ?.split("")
  ?.filter((i) => !specialChar.includes(i))
  ?.join("");

  if (!keywords?.length && params.keyword?.length) {
    return Promise.resolve(new Map<string, number>());
  }

  const facetData = await client.search(keywords, {
    facets: ["productCategory1", "productCategory2", "productCategory3"],
    filter: filterArray,
  });

  // 创建一个 Map 来存储所有分类及其对应的数量
  const categoryCountMap = new Map<string, number>();

  // 处理 facetDistribution 数据
  const { facetDistribution } = facetData;
  if (facetDistribution) {
    Object.entries(facetDistribution).forEach(([level, categories]) => {
      Object.entries(categories).forEach(([category, count]) => {
        categoryCountMap.set(category, count);
      });
    });
  }

  return categoryCountMap;
}

/** 获取应用分类数量统计 */
async function getApplicationCategoryFacet(params: {
  lang: number;
  site: number;
  keyword: string;
  filter?: Filter[];
}) {
  const client = getIndex(params.site);

  const productFilter = params.filter?.find((item) => item.type === "product");

  const filterArray: string[] = ["type = 'application'"];

  if (productFilter && productFilter?.level === 1) {
    filterArray.push(
      `productCategory${productFilter.level} = '${productFilter.value}'`
    );
  }

  await client.updateFilterableAttributes([
    "newsCategory",
    "type",
    "applicationCategory1",
    "applicationCategory2",
    "productCategory1",
    "productCategory2",
    "productCategory3",
    "techDoc1",
    "techDoc2",
    "techDoc3",
    "appNote",
    "evmGuide",
  ]);

   // 过滤特殊字符
   const keywords = params.keyword
   ?.split("")
   ?.filter((i) => !specialChar.includes(i))
   ?.join("");
 
   if (!keywords?.length && params.keyword?.length) {
     return Promise.resolve(new Map<string, number>());
   }

  const facetData = await client.search(params.keyword, {
    facets: ["applicationCategory1", "applicationCategory2"],
    filter: filterArray,
  });

  // 创建一个 Map 来存储所有分类及其对应的数量
  const categoryCountMap = new Map<string, number>();

  // 处理 facetDistribution 数据
  const { facetDistribution } = facetData;
  if (facetDistribution) {
    Object.entries(facetDistribution).forEach(([level, categories]) => {
      Object.entries(categories).forEach(([category, count]) => {
        categoryCountMap.set(category, count);
      });
    });
  }

  return categoryCountMap;
}

/** 获取产品技术文档统计 */
async function getProductTechDocFacet(params: {
  site: number;
  lang: number;
  keyword: string;
  filter?: Filter[];
}) {
  const client = getIndex(params.site);

  const applicationFilter = params.filter?.find(
    (item) => item.type === "application"
  );

  const productFilter = params.filter?.find((item) => item.type === "product");

  const filterArray: string[] = ["type = 'product'"];

  if (applicationFilter && applicationFilter?.level === 1) {
    filterArray.push(`applicationCategory1 = '${applicationFilter.value}'`);
  }

  if (productFilter) {
    filterArray.push(
      `productCategory${productFilter.level} = '${productFilter.value}'`
    );
  }

  await client.updateFilterableAttributes([
    "newsCategory",
    "type",
    "applicationCategory1",
    "applicationCategory2",
    "productCategory1",
    "productCategory2",
    "productCategory3",
    "techDoc1",
    "techDoc2",
    "techDoc3",
    "appNote",
    "evmGuide",
  ]);

   // 过滤特殊字符
   const keywords = params.keyword
   ?.split("")
   ?.filter((i) => !specialChar.includes(i))
   ?.join("");
 
   if (!keywords?.length && params.keyword?.length) {
     return Promise.resolve(new Map<string, number>());
   }

  const facetData = await client.search(params.keyword, {
    facets: ["techDoc1", "techDoc2", "techDoc3", "appNote", "evmGuide"],
    filter: filterArray,
  });

  // 创建一个 Map 来存储所有分类及其对应的数量
  const categoryCountMap = new Map<string, number>();

  // 处理 facetDistribution 数据
  const { facetDistribution } = facetData;
  if (facetDistribution) {
    Object.entries(facetDistribution).forEach(([level, categories]) => {
      Object.entries(categories).forEach(([_, count]) => {
        categoryCountMap.set(level, count);
      });
    });
  }

  return categoryCountMap;
}

/** 获取新闻分类统计 */
async function getNewsCategoryFacet(params: {
  site: number;
  lang: number;
  keyword: string;
}) {
  const client = getIndex(params.site);

  await client.updateFilterableAttributes([
    "newsCategory",
    "type",
    "applicationCategory1",
    "applicationCategory2",
    "productCategory1",
    "productCategory2",
    "productCategory3",
    "techDoc1",
    "techDoc2",
    "techDoc3",
    "appNote",
    "evmGuide",
  ]);

   // 过滤特殊字符
   const keywords = params.keyword
   ?.split("")
   ?.filter((i) => !specialChar.includes(i))
   ?.join("");
 
   if (!keywords?.length && params.keyword?.length) {
     return Promise.resolve(new Map<string, number>());
   }

  const facetData = await client.search(params.keyword, {
    facets: ["newsCategory"],
    filter: `type = news`,
  });

  // 创建一个 Map 来存储所有分类及其对应的数量
  const categoryCountMap = new Map<string, number>();

  // 处理 facetDistribution 数据
  const { facetDistribution } = facetData;
  if (facetDistribution) {
    Object.entries(facetDistribution).forEach(([level, categories]) => {
      Object.entries(categories).forEach(([_, count]) => {
        categoryCountMap.set(level, count);
      });
    });
  }

  return categoryCountMap;
}

export const useProductCategory = (params: {
  site: number;
  lang: number;
  keyword: string;
  filter?: Filter[];
}) => {
  const isApplicationFilter = useMemo(() => {
    return (
      params.filter?.find((item) => item.type === "application")?.level === 1
    );
  }, [params.filter]);

  const { data: categoryData, isLoading: isCategoryLoading } = useSWR(
    () =>
      params.site
        ? `/api/product_category/${params.keyword}/${params.site}/${params.lang}`
        : null,
    async () => {
      const res = await getProductCategory({
        site: params.site,
        lang: params.lang,
      });

      return res.data ?? [];
    }
  );

  const { data: facetData, isLoading: isFacetLoading } = useSWR(
    () =>
      params.site
        ? `/api/product_category/meilisearch/${params.keyword}/${params.site}/${params.lang}/${isApplicationFilter}`
        : null,
    async () => {
      return await getProductCategoryFacet({
        site: params.site,
        lang: params.lang,
        keyword: params.keyword,
        filter: params.filter,
      });
    }
  );

  const data = useMemo(() => {
    if (categoryData && facetData) {
      const data = categoryData?.map((item) => {
        const categoryName =
          item.lang_data.find((l) => l.lang === params.lang.toString())?.name ||
          item.name;

        return {
          ...item,
          categoryName,
          count: facetData.get(categoryName) || 0,
        };
      });

      return getTree(data, null, params.lang);
    }
  }, [categoryData, facetData, params.lang]);

  return {
    data,
    isLoading: isCategoryLoading || isFacetLoading,
  };
};

export const useApplicationCategory = (params: {
  site: number;
  lang: number;
  keyword: string;
  filter?: Filter[];
}) => {
  const isProductFilter = useMemo(() => {
    return params.filter?.find((item) => item.type === "product")?.level === 1;
  }, [params.filter]);

  const { data: categoryData, isLoading: isCategoryLoading } = useSWR(
    () =>
      params.site
        ? `/api/application_category/${params.keyword}/${params.site}/${params.lang}`
        : null,
    async () => {
      const res = await getApplicationCategory({
        site: params.site,
        lang: params.lang,
      });

      return res.data ?? [];
    }
  );

  const { data: facetData, isLoading: isFacetLoading } = useSWR(
    () =>
      params.site
        ? `/api/application_category/meilisearch/${params.keyword}/${params.site}/${params.lang}/${isProductFilter}`
        : null,
    async () => {
      return await getApplicationCategoryFacet({
        site: params.site,
        lang: params.lang,
        keyword: params.keyword,
        filter: params.filter,
      });
    }
  );

  const data = useMemo(() => {
    if (categoryData && facetData) {
      const data = categoryData?.map((item) => {
        const categoryName =
          item.lang_data.find((l) => l.lang === params.lang.toString())?.name ||
          item.name;

        return {
          ...item,
          categoryName,
          count: facetData.get(categoryName) || 0,
        };
      });

      return getTree(data, null, params.lang);
    }
  }, [categoryData, facetData, params.lang]);

  return { data, isLoading: isCategoryLoading || isFacetLoading };
};

export const useNewsCategory = (params: {
  site: number;
  lang: number;
  keyword: string;
}) => {
  const { data, isLoading } = useSWR(
    () =>
      params.site
        ? `/api/news_category/${params.keyword}/${params.site}/${params.lang}`
        : null,
    async () => {
      const res = await getNewsCategory({
        site: params.site,
        lang: params.lang,
      });

      const facet = await getNewsCategoryFacet({
        site: params.site,
        lang: params.lang,
        keyword: params.keyword,
      });

      return res?.data?.map((item) => {
        const categoryName =
          item.lang_data.find((l) => l.lang === params.lang.toString())?.name ||
          item.name;

        return {
          id: item.id,
          title: categoryName,
          count: facet.get(categoryName) || 0,
        };
      });
    }
  );

  return { data, isLoading };
};

export const useProductTechDoc = (params: {
  site: number;
  lang: number;
  keyword: string;
  filter?: Filter[];
}) => {
  const { user } = useUser();

  const { data: techDocData, isLoading: isTechDocLoading } = useSWR(
    () =>
      params.site
        ? `/api/product_tech_doc/${params.keyword}/${params.site}/${
            params.lang
          }/${JSON.stringify(params.filter)}/${user?.id}`
        : null,
    async () => {
      const res = await getProductTechDocFacet({
        site: params.site,
        lang: params.lang,
        keyword: params.keyword,
        filter: params.filter,
      });

      return filterTechDoc(res, user?.role?.level);
    }
  );

  return { data: techDocData, isLoading: isTechDocLoading };
};
